import { createContext, useCallback, useContext, useMemo, useState } from "react";
import Button from "../../common/components/Button";
import { Col, Row } from "../../common/components/Grid";
import TextInput from "../../common/components/TextInput";
import Card from "../../common/components/Card";
import Checkbox from "../../common/components/Checkbox";
import Range from "../../common/components/Range";
import { DefaultButton } from "../../common/components/DefaultButton";

import propTypes from 'prop-types';
import Icon from "../../common/components/Icon";
import { distinct } from "../../common/utils/arrayUtilities";
import { useTranslation } from "react-i18next";

const LayerManagerContext = createContext();

export const useLayerManager = () => useContext(LayerManagerContext);

export const LayerManager = ({ children }) => {
    const { t } = useTranslation();
    const [layers, setLayers] = useState({});
    const [showLayerPicker, setShowLayerPicker] = useState(false);
    const [filter,setFilter] = useState();

    const registerLayer = useCallback((id, title, group, kind, visible, opacity) => {
        setLayers(prev => ({...prev, [id]: { title, group, kind, visible, opacity }}));
    }, [setLayers]);

    const unregisterLayer = useCallback(id => {
        setLayers(prev => {
            const newLayers = {};
            Object.keys(prev).filter(f => f !== id).forEach(f => newLayers[f] = prev[f]);
            return newLayers;
        })
    }, [setLayers]);

    const isVisible = useCallback(id => !!layers[id]?.visible, [layers]);
    const getOpacity = useCallback(id => layers[id]?.opacity ?? 0, [layers]);

    const contextValue = useMemo(() => ({ registerLayer, unregisterLayer, isVisible, getOpacity }), [registerLayer, unregisterLayer, isVisible, getOpacity]);

    let groups = distinct(Object.values(layers).map(l => l.group));
    groups.sort((g1, g2) => {
        if (!g1 && g2) {
            return -1;
        } else if (g1 && !g2) {
            return 1;
        } else if (g1?.toLowerCase() < g2?.toLowerCase()) {
            return -1;
        } else if (g1?.toLowerCase() > g2?.toLowerCase()) {
            return 1;
        }
        return 0;
    });

    return <LayerManagerContext.Provider value={contextValue}>
        { showLayerPicker ? <div id="mapLayerPicker" tabIndex={0} style={{ position: 'absolute', overflowX: 'hidden', overflowY: 'auto', float: 'right', height: 'calc(100% - 54px)', width: '300px', top: '54px', right: '0', backgroundColor: 'white', padding: '4px', zIndex: 4 }}>
            <div style={{ position: 'sticky', top: '-4px', zIndex: 3, backgroundColor: 'white', paddingBottom: '8px' }}>
            <Row >
                <Col className="text-right mr-2">
                    <DefaultButton kind="close" variant="outline-dark" size="sm" hideLabel={true} onClick={() => setShowLayerPicker(false)}></DefaultButton>
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextInput placeholder="Filter" live={true} value={filter} onChange={setFilter} prependText={<Icon intent="filter"></Icon>}></TextInput>
                </Col>
            </Row>
            </div>
            {groups.map(g => {
                let groupLayers = Object.keys(layers).filter(l => layers[l].group === g);
                const allVisible = groupLayers.every(l => layers[l].visible);
                const toggleAll = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLayers(prev => {
                        let layers = {...prev};
                        groupLayers.forEach(l => {
                            layers[l] = ({ ...prev[l], visible: !allVisible })
                        });
                        return layers;
                    });
                };
                if (filter && !g?.toLowerCase().includes(filter.toLowerCase()) && !groupLayers.some(l => layers[l].title?.toLowerCase().includes(filter.toLowerCase()))) {
                    return null;
                }
                if (filter && !g?.toLowerCase().includes(filter.toLowerCase())) {
                    groupLayers = groupLayers.filter(l => layers[l].title?.toLowerCase().includes(filter.toLowerCase()));
                }
                groupLayers.sort();
                return <Row key={g}>
                    <Col>
                        <Card title={g} headerControls={<Button variant="link" size="sm" onClick={toggleAll}>{allVisible ? t('hideAll') : t('showAll')}</Button>}>
                            {groupLayers.map(l => {
                                const layer = layers[l];
                                return <Row key={l}>
                                    <Col xs={7}>
                                        <Checkbox checked={layer.visible} placeholder={layer.title} hideLabel={true} onChange={value => setLayers(prev => ({...prev, [l]: {...layer, visible: !layer.visible}}))}></Checkbox>
                                    </Col>
                                    <Col xs={5}>
                                        <Range placeholder={l ?? ''} hideLabel={true} min={0} max={1} step={0.05} value={layer.opacity} live={true} onChange={value => setLayers(prev => ({...prev, [l]: {...layer, opacity: value}}))}></Range>
                                    </Col>
                                </Row>}
                            )}
                        </Card>
                    </Col>
                </Row>})}
        </div> : <div style={{position: 'absolute', top: '72px', right: '36px', zIndex: 5}}>
                <Button variant="solid-primary" onClick={() => setShowLayerPicker(true)}><Icon variant="solid" kind="layer-group"></Icon></Button>
        </div> }
        {children}
    </LayerManagerContext.Provider>
}

LayerManager.propTypes = {
    children: propTypes.node.isRequired
}